import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getPermissionsInfo } from '../api/admin';
import { useLanguage } from '../i18n/LanguageContext';
import GlobalBar from './GlobalBar';
import styles from './Layout.module.css';

const ROLE_LABELS = {
  company_director: 'Company Director',
  super_admin: 'Super Admin',
  country_manager: 'Country Manager',
  support_agent: 'Support Agent',
  contractor: 'Contractor',
};

// Permissions & History Development Plan (13.10.2026), revised per Kayd's
// 14.10.2026 navigation follow-up — the restricted account's menu: My
// Tasks stays a separate top-level item (the personal work inbox), and
// Case Control has exactly two sub-items for this account type: "My
// Current Cases" and "My History" (renamed from "Current Assignments" /
// "History" for clarity against the Owner's "Case Overview" /
// "History & Document Rights"). Scoped to the person's own work by the
// server (see routes/caseControl.js current-assignments & history). This
// is a display convenience only: the real boundary is the server-side
// allowlist in lib/contractorScope.js, which blocks every other route
// even via a direct URL -- a restricted account can never reach History &
// Document Rights or the full Activity Log this way, confirmed live.
const CONTRACTOR_GROUPS = [
  { id: 'task-inbox', standalone: true, label: 'My Tasks', icon: '📥', to: '/task-inbox', visible: true },
  {
    id: 'case-control-restricted', label: 'Case Management', icon: '🗂️', visible: true,
    items: [
      { id: 'case-control-current', label: 'My Current Cases', to: '/case-control/current' },
      { id: 'case-control-history', label: 'My History', to: '/case-control/history' },
    ],
  },
];

// ============================================================================
// NULA MOVE · ADMIN — navigation restructure (Ikram's spec, Phase 1):
// layout, 8-section navigation, country selector, role-based visibility.
//
// Deliberately NOT done in this pass (matches the client's own suggested
// phasing): renaming existing URLs (every route below still points at its
// original, already-working path — nothing to redirect because nothing
// moved), the Task Inbox / global search content (Phase 3), and Bookings &
// Finance / support tickets / Integrations & API content (Phase 4). Those
// sections' PLACES in the navigation exist now (pointing at a clearly
// labelled "coming soon" page) so the overall shape is already correct.
// ============================================================================
function buildNavGroups(permissions, isOwnerOrAdmin) {
  const vis = (perm) => !permissions || permissions[perm];

  const groups = [
    { id: 'dashboard', standalone: true, label: 'Dashboard', icon: '📊', to: '/dashboard', visible: true },
    { id: 'task-inbox', standalone: true, label: 'My Tasks', icon: '📥', to: '/task-inbox', visible: true },
    // Case Control brief: the full unified queue is still gated on
    // manage_case_control -- "Only owners/company directors and explicitly
    // approved global admins see this view." Permissions & History
    // Development Plan (13.10.2026): everyone ELSE still gets a Case
    // Control entry, scoped to their own current assignments and history
    // (see routes/caseControl.js current-assignments & history) -- "See
    // own assigned assignments: Yes, always on" is a baseline right, not
    // something manage_case_control gates.
    // Kayd's 14.10.2026 navigation follow-up — Case Control becomes a
    // collapsible group for the Owner/authorized global admin too, with
    // three sub-items: Case Overview (the full queue, same page/route as
    // before, renamed), History & Document Rights (the Phase 4 settings
    // page — previously reachable only via an in-page button, now a real
    // nav entry), and Activity Log (same route the System section already
    // links to -- kept there too, this is additive, not a move, so
    // nothing existing breaks for anyone used to finding it under System).
    vis('manage_case_control')
      ? {
          id: 'case-control', label: 'Case Management', icon: '🗂️', visible: true,
          items: [
            { id: 'case-control-overview', label: 'Case Overview', to: '/case-control', end: true },
            { id: 'case-control-settings', label: 'History & Document Rights', to: '/case-control/settings' },
            // Kayd's 15.10.2026 follow-up: Activity Log lives here only (the
            // System copy is removed). Same visibility as the old System
            // entry and the page's own RequireOwnerOrAdmin route guard.
            ...(isOwnerOrAdmin ? [{ id: 'case-control-activity-log', label: 'Activity Log', to: '/activity-log' }] : []),
          ],
        }
      : {
          id: 'case-control-restricted', label: 'Case Management', icon: '🗂️', visible: true,
          items: [
            { id: 'case-control-current', label: 'My Current Cases', to: '/case-control/current' },
            { id: 'case-control-history', label: 'My History', to: '/case-control/history' },
          ],
        },
    {
      id: 'onboarding', label: 'Onboarding & Approvals', icon: '✅',       // Ride Operations is independently gated by manage_country_fees; keep
      // the Marketplace group discoverable for fee administrators who do not
      // manage applications.
      visible: vis('manage_applications') || vis('manage_country_fees'),
      items: [
        { id: 'applications', label: 'Applications', children: [
          { to: '/applications/all', label: 'All' },
          { to: '/applications/submitted', label: 'Submitted' },
          { to: '/applications/changes_requested', label: 'Changes Requested' },
          { to: '/applications/approved', label: 'Approved' },
          { to: '/applications/rejected', label: 'Rejected' },
          { to: '/applications/suspended', label: 'Suspended' },
        ] },
        { id: 'fleet-approvals', label: 'Fleet Approvals', to: '/fleet-approvals', visible: vis('manage_staff') },
        // Kenya provider applications (all types) — the page existed but had no menu entry.
        { id: 'provider-applications', label: 'Provider Applications (Kenya)', to: '/provider-applications' },
        { id: 'verifications', label: 'Verifications', children: [
          { to: '/verifications/all', label: 'All' },
          { to: '/verifications/pending', label: 'Pending' },
          { to: '/verifications/verified', label: 'Verified' },
        ] },
      ].filter((i) => i.visible !== false),
    },
    {
      id: 'marketplace', label: 'Marketplace', icon: '🏪', visible: vis('manage_applications'),
      items: [
        { id: 'users', label: 'Users', children: [
          { to: '/users/all', label: 'All Users' },
          { to: '/users/active', label: 'Active' },
          { to: '/users/blocked', label: 'Blocked' },
        ] },
        { id: 'mobility', label: 'Mobility', children: [
          { to: '/vehicles', label: 'Vehicles' },
          { to: '/ride-operations', label: 'Ride Operations', visible: vis('manage_country_fees') },
          { to: '/carshare-registrations', label: 'Carshare Regs' },
          { to: '/protocols', label: 'Protocols' },
        ] },
      ].filter((i) => i.visible !== false),
    },
    {
      // Replaces the old single "Rooms" nav item per the client's restructure
      // spec — same underlying data, reorganized into category-filtered tabs.
      id: 'accommodation', label: 'Accommodation/Providers', icon: '🏨', visible: vis('manage_rooms'),
      items: [
        { id: 'accommodation-overview', label: 'Overview', to: '/accommodation/overview' },
        // Kayd 18.10.2026: host applications (with documents) are reviewed here;
        // the host-account status page (verify / block) keeps its own entry.
        { id: 'accommodation-provider-applications', label: 'Host Applications', to: '/accommodation/provider-applications' },
        { id: 'accommodation-hosts', label: 'Host Accounts', to: '/accommodation/hosts' },
        { id: 'accommodation-hotels', label: 'Hotels', to: '/accommodation/listings/hotel' },
        { id: 'accommodation-vacation-rentals', label: 'Vacation Rentals', to: '/accommodation/listings/vacation_rental' },
        { id: 'accommodation-wedding-halls', label: 'Wedding Halls', to: '/accommodation/listings/wedding_hall' },
        { id: 'accommodation-events', label: 'Events', to: '/accommodation/listings/event' },
        { id: 'accommodation-all-listings', label: 'All Listings', to: '/accommodation/listings' },
        { id: 'accommodation-calendar', label: 'Availability & Calendar', to: '/accommodation/calendar' },
        { id: 'accommodation-bookings', label: 'Bookings', to: '/accommodation/bookings' },
        { id: 'accommodation-fees', label: 'Fees & Taxes', to: '/fees' },
        { id: 'accommodation-reviews', label: 'Reviews & Reports', to: '/accommodation/reviews' },
      ],
    },
    {
      id: 'safety-support', label: 'Safety & Support', icon: '🛟', visible: vis('manage_safety') || vis('manage_damage_cases'),
      items: [
        { id: 'damage-cases', label: 'Damage Cases', visible: vis('manage_damage_cases'), children: [
          { to: '/damage-cases', label: 'Cases' },
          { to: '/all-damage-reports', label: 'All Reports (incl. carshare)' },
        ] },
        { id: 'safety-reports', label: 'Safety Center', to: '/safety-reports', visible: vis('manage_safety') },
        { id: 'support-tickets', label: 'Support Tickets', to: '/support-tickets' },
        { id: 'driver-training', label: 'Driver Safety Training', to: '/driver-safety-training' },
      ].filter((i) => i.visible !== false),
    },
    {
      id: 'rules-content', label: 'Rules & Content', icon: '📐', visible: vis('manage_applications'),
      items: [
        { id: 'category-config', label: 'Home Page Categories', to: '/category-config' },
        { id: 'marketplace-categories', label: 'Marketplace Categories', to: '/marketplace-categories', visible: vis('manage_rooms') },
        { id: 'fees-taxes', label: 'Fees & Taxes', to: '/fees' },
        { id: 'vehicle-owner-requirements', label: 'Vehicle Lessor Requirements', to: '/vehicle-owner-requirements' },
        { id: 'legal-texts', label: 'Legal Texts & Templates', to: '/legal-texts' },
      ],
    },
    { id: 'bookings-finance', standalone: true, label: 'Bookings & Finance', icon: '💰', to: '/bookings-finance', visible: true },
  ];

  return groups.filter((g) => g.visible);
}

// Kayd's 15.10.2026 follow-up: the duplicate Activity Log entry is removed
// from System -- it lives under Case Management. Only fallback: an
// Owner/Global Admin whose manage_case_control has been switched off gets
// the restricted Case Management group (no Activity Log there), so they
// keep the System entry rather than losing the page from the menu.
function buildSystemGroup(isOwnerOrAdmin, hasCaseManagementLog) {
  return {
    id: 'system', label: 'System', icon: '⚙️', visible: isOwnerOrAdmin,
    items: [
      { id: 'staff', label: 'Team & Roles', to: '/staff' },
      ...(hasCaseManagementLog ? [] : [{ id: 'logs', label: 'Activity Log', to: '/activity-log' }]),
      { id: 'countries-settings', label: 'Countries & Settings', to: '/countries-settings' },
      { id: 'data-quality', label: 'Data Quality', to: '/data-quality' },
      { id: 'integrations', label: 'Integrations & API', to: '/integrations' },
    ],
  };
}

export default function Layout() {
  const { user, adminRole, permissions } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: permData } = useQuery({
    queryKey: ['permissions-info'],
    queryFn: () => getPermissionsInfo().then((r) => r.data),
    staleTime: 60_000,
    retry: false,
  });

  const effectivePermissions = permissions || permData?.permissions || {};
  const effectiveRole = adminRole || permData?.adminRole || null;
  const isOwnerOrAdmin = ['company_director', 'super_admin'].includes(effectiveRole?.role_type) || !effectiveRole;
  const isContractor = effectiveRole?.role_type === 'contractor';

  const GROUPS = isContractor
    ? CONTRACTOR_GROUPS
    : [...buildNavGroups(effectivePermissions, isOwnerOrAdmin), buildSystemGroup(isOwnerOrAdmin, !!effectivePermissions.manage_case_control)].filter((g) => g.visible);

  // Display-only companion to the server-side allowlist: the API already
  // refuses the data for any other page, this just avoids showing a
  // contractor a blank/broken page shell if they land on one directly.
  useEffect(() => {
    const allowed = location.pathname.startsWith('/task-inbox') || location.pathname.startsWith('/case-control/current') || location.pathname.startsWith('/case-control/history');
    if (isContractor && !allowed) {
      navigate('/task-inbox', { replace: true });
    }
  }, [isContractor, location.pathname, navigate]);

  const [openItem, setOpenItem] = useState(null);

  const roleDisplay = effectiveRole?.role_type ? ROLE_LABELS[effectiveRole.role_type] || effectiveRole.role_type : 'Administrator';
  // Longest matching path wins, so /case-control/settings is titled
  // "History & Document Rights", not "Case Overview" (/case-control).
  const matchLength = (i) => Math.max(
    i.to && location.pathname.startsWith(i.to) ? i.to.length : -1,
    ...(i.children || []).map((c) => (location.pathname.startsWith(c.to) ? c.to.length : -1)),
  );
  const pageTitle = GROUPS.flatMap((g) => (g.to ? [g] : (g.items || [])))
    .reduce((best, i) => (matchLength(i) > (best ? matchLength(best) : -1) ? i : best), null)?.label || 'Dashboard';

  return (
    <div className={styles.shell}>
      {menuOpen && <div className={styles.overlay} onClick={() => setMenuOpen(false)} />}

      <aside className={`${styles.sidebar} ${menuOpen ? styles.open : ''}`}>
        <div className={styles.brand}>
          <div className={styles.brandLogo}>
            <img src="/admin-new-logo.png" alt="Nula Move" className={styles.brandLogoImg} />
          </div>
          <div>
            <span className={styles.brandName}>Nula Move</span>
            <span className={styles.brandSub}>Admin Panel</span>
          </div>
        </div>

        {user && (
          <div className={styles.adminBadge}>
            <div className={styles.adminAvatar}>{(user.full_name || user.email || '?')[0].toUpperCase()}</div>
            <div className={styles.adminInfo}>
              <span className={styles.adminName}>{user.full_name || 'Admin'}</span>
              <span className={styles.adminRole}>{roleDisplay}</span>
            </div>
          </div>
        )}

        <nav className={styles.nav}>
          {GROUPS.map((group) => {
            if (group.standalone) {
              return (
                <NavLink key={group.id} to={group.to} className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ''}`} onClick={() => setMenuOpen(false)}>
                  <span className={styles.navIcon}>{group.icon}</span>
                  <span className={styles.navLabel}>{t(group.label)}</span>
                </NavLink>
              );
            }

            return (
              <div key={group.id} style={{ marginTop: 10 }}>
                <div style={{ padding: '4px 14px', fontSize: 10.5, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {group.icon} {t(group.label)}
                </div>
                {group.items.map((item) => {
                  if (item.to && !item.children) {
                    return (
                      <NavLink key={item.id} to={item.to} end={!!item.end} className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ''}`} onClick={() => setMenuOpen(false)}>
                        <span className={styles.navLabel}>{t(item.label)}</span>
                      </NavLink>
                    );
                  }
                  const itemOpen = openItem === item.id || (item.children || []).some((c) => location.pathname.startsWith(c.to));
                  return (
                    <div key={item.id} className={styles.section}>
                      <button className={styles.sectionHeader} onClick={() => setOpenItem((prev) => (prev === item.id ? null : item.id))} aria-expanded={itemOpen}>
                        <span className={styles.navLabel}>{t(item.label)}</span>
                        <span className={`${styles.chevron} ${itemOpen ? styles.chevronOpen : ''}`}>›</span>
                      </button>
                      {itemOpen && (
                        <div className={styles.sectionChildren}>
                          {item.children.map((child) => (
                            <NavLink key={child.to} to={child.to} className={({ isActive }) => `${styles.childLink} ${isActive ? styles.childLinkActive : ''}`} onClick={() => setMenuOpen(false)}>
                              <span>{t(child.label)}</span>
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className={styles.main}>
        <GlobalBar title={t(pageTitle)} onMenuToggle={() => setMenuOpen((v) => !v)} />
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
